/* eslint-disable no-underscore-dangle */
import GridBox from '@components/atoms/GridBox';
import Section from '@components/atoms/Section';
import Spacing from '@components/atoms/Spacing';
import { Heading1, Heading3, Paragraph } from '@components/atoms/Typography';
import PageContainer from '@components/molecules/PageContainer';
import BREAKPOINTS from '@lib/constants/breakpoints';
import COLORS from '@lib/constants/colors';
import env from '@lib/constants/env';
import ROUTES from '@lib/constants/routes';
import {
  getAllInsuranceTermsNodes,
  InsuranceTermsNode,
  PrismicClient,
} from '@lib/services/prismic';
import { GetStaticProps } from 'next';
import { BreadcrumbJsonLd, SiteLinksSearchBoxJsonLd } from 'next-seo';
import Link from 'next/link';
import { RichText } from 'prismic-reactjs';
import React from 'react';
import styled from 'styled-components';
import { InsuropediaDocument } from 'types/frontEndTypes';
import Image from 'next/image';

const Header = styled(Section)`
  padding: 128px 64px 64px;

  h1 {
    font-size: 64px;
  }

  p {
    font-size: 20px;
  }

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    h1 {
      font-size: 48px;
      text-align: center;
    }

    img {
      margin-top: 32px;
      max-width: 300px;
    }

    p {
      font-size: 16px;
      text-align: center;
    }

    a,
    button {
      width: 100%;
    }
  }
`;

const Grid = styled.div`
  flex-wrap: wrap;
  margin: 0 auto;

  text-align: center;
  display: flex;
`;

const InnerGrid = styled.div`
  grid-gap: 32px;
  margin: 0 auto;
  width: 100%;
  text-align: center;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin-bottom: 48px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    grid-template-columns: 1fr;
  }
`;

const GlossaryItem = styled.div`
  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding-right: 25px;
    padding-left: 25px;
  }
`;

const GlossaryItemInner = styled.div`
  height: 100%;
  position: relative;
  border-radius: 4px;
  background-color: #fff;
`;

const GlossaryItemInnerLetter = styled(GlossaryItemInner)`
  display: flex;
  justify-content: center;
  align-items: center;
  h3 {
    margin-top: 0;
    font-size: 144px;
  }
`;

const GlossaryItemInnerTerm = styled(GlossaryItemInner)`
  box-shadow: 0 10px 30px rgb(0 0 0 / 5%);
  padding: 40px;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.1);
  }

  h3 {
    margin: 0 0 16px;
    color: ${COLORS.aqua};
    text-decoration: underline;
  }

  p {
    line-height: 1.5;
  }
`;

interface Props {
  terms: InsuranceTermsNode[];
  insuropediaDocument: InsuropediaDocument;
  preview: boolean;
}

const Seguropedia = ({ terms, preview, insuropediaDocument }: Props) => {
  const groupedTerms = terms
    .map((t) => ({ firstLetter: RichText.asText(t.title)[0], ...t }))
    .sort((a, b) =>
      RichText.asText(a.title).localeCompare(RichText.asText(b.title)),
    )
    .reduce(
      (result: Record<string, InsuranceTermsNode[]>, item) => ({
        ...result,
        [item.firstLetter]: [...(result[item.firstLetter] || []), item],
      }),
      {},
    );

  const url = env.baseUrl + ROUTES.insuropedia;

  return (
    <PageContainer
      preview={preview}
      title={insuropediaDocument.data.seo[0].title}
      metaDescription={insuropediaDocument.data.seo[0].description}
    >
      <SiteLinksSearchBoxJsonLd
        url={url}
        potentialActions={[
          {
            target: `${url}/busca?q`,
            queryInput: 'term',
          },
        ]}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Luna',
            item: env.baseUrl,
          },
          {
            position: 2,
            name: 'Glossário de Seguros',
            item: env.baseUrl + ROUTES.insuropedia,
          },
        ]}
      />

      <Header>
        <GridBox alignItems="center">
          <GridBox width={[1, 0.4, 0.4]}>
            <Heading1>
              {RichText.asText(insuropediaDocument.data.header[0].title)}
            </Heading1>
            <Spacing vertical={2}>
              <Paragraph>
                {RichText.asText(insuropediaDocument.data.header[0].tagline)}
              </Paragraph>
            </Spacing>
          </GridBox>
          <GridBox justifyContent="center" width={[1, 0.6, 0.6]}>
            {insuropediaDocument.data.header[0].image.url && (
              <Image
                src={insuropediaDocument.data.header[0].image.url}
                alt={insuropediaDocument.data.header[0].image.alt}
                height={
                  insuropediaDocument.data.header[0].image.dimensions?.height ||
                  0
                }
                width={
                  insuropediaDocument.data.header[0].image.dimensions?.width ||
                  0
                }
              />
            )}
          </GridBox>
        </GridBox>
      </Header>
      <Section pastel>
        <Grid>
          {Object.entries(groupedTerms)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([firstLetter, actualTerms]) => (
              <InnerGrid key={firstLetter}>
                <GlossaryItem>
                  <GlossaryItemInnerLetter>
                    <Heading3>{firstLetter}</Heading3>
                  </GlossaryItemInnerLetter>
                </GlossaryItem>
                {actualTerms.map((term) => (
                  <GlossaryItem key={term._meta.uid}>
                    <Link
                      key={term._meta.uid}
                      href={ROUTES.insuranceTerm(term._meta.uid)}
                    >
                      <a>
                        <GlossaryItemInnerTerm>
                          <Heading3>{RichText.asText(term.title)}</Heading3>
                          <Paragraph>
                            {RichText.asText(term.description)}
                          </Paragraph>
                        </GlossaryItemInnerTerm>
                      </a>
                    </Link>
                  </GlossaryItem>
                ))}
              </InnerGrid>
            ))}
        </Grid>
      </Section>
    </PageContainer>
  );
};

export const getStaticProps: GetStaticProps<Props> = async ({
  preview = false,
  previewData,
}) => {
  const [insuropediaDocument, terms] = await Promise.all([
    PrismicClient.getSingle('seguropedia', previewData as Record<string, any>),
    getAllInsuranceTermsNodes(),
  ]);

  return {
    props: {
      terms: terms?.map((t) => t.node) || [],
      preview,
      insuropediaDocument: insuropediaDocument as InsuropediaDocument,
    },
    revalidate: 3600,
  };
};

export default Seguropedia;
